/**
 * Affiliate portal option sets.
 *
 * Country ids come from the shared `COUNTRIES` list — the same ids the member
 * profile writes as `countryId` — so the affiliate form cannot drift onto a
 * second numbering. The three markets the programme launched in are pinned to
 * the top of the list; the rest stay alphabetical.
 */
import { COUNTRIES } from './countries';

export interface AffiliateOption {
  /** Empty string for the placeholder row, so a required check catches it. */
  value: string;
  labelKey: string;
}

export interface AffiliateCountryOption {
  id: number;
  label: string;
}

const PINNED_COUNTRY_CODES: readonly string[] = ['US', 'CA', 'GB'];

const pinned = PINNED_COUNTRY_CODES.map(code =>
  COUNTRIES.find(country => country.value === code),
).filter((country): country is (typeof COUNTRIES)[number] => country != null);

const rest = COUNTRIES.filter(
  country => !PINNED_COUNTRY_CODES.includes(country.value),
).sort((a, b) => a.label.localeCompare(b.label));

export const AFFILIATE_COUNTRIES: readonly AffiliateCountryOption[] = [
  ...pinned,
  ...rest,
].map(country => ({ id: country.id, label: country.label }));

/** Resolve a stored country name (what the API reads back) to its id. */
export const affiliateCountryIdFromName = (name: string): number | null => {
  const needle = name.trim().toLowerCase();
  if (!needle) return null;
  const match = AFFILIATE_COUNTRIES.find(
    country => country.label.toLowerCase() === needle,
  );
  return match ? match.id : null;
};

export const affiliateCountryNameFromId = (id: number | null): string => {
  const match = AFFILIATE_COUNTRIES.find(country => country.id === id);
  return match ? match.label : '';
};

/**
 * "How did you hear about WLB?" answers. Values are sent verbatim, so they are
 * stable identifiers rather than display copy; `labelKey` carries the copy.
 * `Other` reveals a free-text field whose value is sent in place of the option.
 */
export const AFFILIATE_HOW_HEARD_OTHER = 'Other';

export const AFFILIATE_HOW_HEARD_OPTIONS: readonly AffiliateOption[] = [
  { value: 'Google Search', labelKey: 'affiliate.howHeard.googleSearch' },
  { value: 'WLB Member', labelKey: 'affiliate.howHeard.wlbMember' },
  { value: 'Facebook', labelKey: 'affiliate.howHeard.facebook' },
  { value: 'Instagram', labelKey: 'affiliate.howHeard.instagram' },
  { value: 'TikTok', labelKey: 'affiliate.howHeard.tiktok' },
  { value: 'YouTube', labelKey: 'affiliate.howHeard.youtube' },
  { value: 'X (Twitter)', labelKey: 'affiliate.howHeard.x' },
  { value: 'Reddit', labelKey: 'affiliate.howHeard.reddit' },
  { value: 'LinkedIn', labelKey: 'affiliate.howHeard.linkedIn' },
  { value: 'Pinterest', labelKey: 'affiliate.howHeard.pinterest' },
  { value: AFFILIATE_HOW_HEARD_OTHER, labelKey: 'affiliate.howHeard.other' },
];

/**
 * Landing paths that exist only to attribute a visitor to a campaign before
 * handing them to `/affiliate`. Each stores its own slug as the source, which
 * pre-fills "How did you hear about us" on the application.
 */
export const AFFILIATE_SOURCE_PATHS: readonly string[] = ['ace', 'cee'];
